/**
 * @description Elavon Hosted Payment Fields (HPF) card entry component.
 * Customer portal version — identical UI/UX to frontend, but card fields are
 * Elavon-hosted iFrames (PCI SAQ-A compliant: card data never touches our DOM).
 *
 * Key features:
 * - 4 HPF iFrame containers (card number, expiry, CVV, cardholder name)
 * - Billing address fields rendered locally (same as PaymentCardForm)
 * - Real-time field validation state from HPF messageHandler
 * - No loading spinner — form layout renders immediately, iFrames silently populate
 * - Surcharge handled server-side via POST /surcharge-advices after vaulting (not in SDK)
 * - Same base props interface as PaymentCardForm, plus amount/currency/description
 *   for HPF session creation (PaymentCardForm ignores the extra props)
 *
 * @dependencies
 * - useHostedPaymentFields: Hook managing HPF SDK lifecycle
 * - usStates, addressFormatters: Same billing address validation as PaymentCardForm
 *
 * @notes
 * - The <div> IDs (hpf-card-number, hpf-expiry, etc.) MUST match the wrapperId
 *   values in useHostedPaymentFields.js FIELD_CONFIG.
 * - Card data never enters our DOM — Elavon's SDK renders iFrames inside the divs.
 * - Billing address IS collected locally and passed to HPF submit({ billTo }).
 * - This component is never rendered directly — CardEntry.js chooses between
 *   this and PaymentCardForm based on the active gateway.
 */

import React, { useState, useCallback } from 'react';
import { US_STATES, isValidStateCode } from '../../utils/usStates';
import {
  formatCityName,
  isValidZipCode,
  formatZipCode,
  formatStreetAddress
} from '../../utils/addressFormatters';
import { useHostedPaymentFields } from '../../hooks/useHostedPaymentFields';

function HostedPaymentFields({
  onSubmit,
  processing = false,
  error = null,
  submitButtonText = 'Process Payment',
  theme = 'default',
  amount,
  currency,
  description,
  clientConfig = null,
  customerPublicId = null
}) {
  // Billing address state (collected locally, same fields as PaymentCardForm)
  const [billing, setBilling] = useState({
    street: '',
    city: '',
    state: '',
    zip: ''
  });
  const [billingErrors, setBillingErrors] = useState({});
  const [billingTouched, setBillingTouched] = useState({});

  // HPF hook — manages SDK lifecycle, field validity, submission
  const {
    isReady,
    isRecovering,
    isSubmitting,
    error: hpfError,
    fieldValidity,
    fieldsLoaded,
    submit: hpfSubmit
  } = useHostedPaymentFields({
    amount,
    currency,
    description,
    clientConfig,
    customerPublicId,
    onCardReady: (cardData) => {
      // HPF returned a hostedCard — pass it up to the parent with billing data
      onSubmit({
        ...cardData,
        billingAddress: formatStreetAddress(billing.street),
        billingCity: formatCityName(billing.city),
        billingState: billing.state.toUpperCase(),
        billingZip: formatZipCode(billing.zip)
      });
    },
    onError: () => {
      // Error state is tracked via hpfError — no console logging in production
    }
  });

  // ========================================================================
  // Billing Address Validation (same rules as PaymentCardForm)
  // ========================================================================

  const validateBillingField = useCallback((name, value) => {
    const newErrors = { ...billingErrors };

    switch (name) {
      case 'street':
        if (!value || value.trim().length < 5) {
          newErrors.street = 'Billing address is required (minimum 5 characters)';
        } else {
          delete newErrors.street;
        }
        break;
      case 'city':
        if (!value || value.trim().length < 2) {
          newErrors.city = 'City is required (minimum 2 characters)';
        } else {
          delete newErrors.city;
        }
        break;
      case 'state':
        if (!value) {
          newErrors.state = 'State is required';
        } else if (!isValidStateCode(value)) {
          newErrors.state = 'Please select a valid state';
        } else {
          delete newErrors.state;
        }
        break;
      case 'zip':
        if (!value) {
          newErrors.zip = 'ZIP code is required';
        } else if (!isValidZipCode(value)) {
          newErrors.zip = 'Please enter a valid ZIP code (5 digits or ZIP+4 format)';
        } else {
          delete newErrors.zip;
        }
        break;
      default:
        break;
    }

    setBillingErrors(newErrors);
    return !newErrors[name];
  }, [billingErrors]);

  const handleBillingChange = (e) => {
    const { name, value } = e.target;
    let formatted = value;

    if (name === 'street') formatted = value.slice(0, 100);
    if (name === 'city') formatted = value.slice(0, 50);
    if (name === 'zip') formatted = formatZipCode(value);

    setBilling(prev => ({ ...prev, [name]: formatted }));

    if (billingTouched[name]) {
      validateBillingField(name, formatted);
    }
  };

  const handleBillingBlur = (e) => {
    const { name, value } = e.target;
    let finalValue = value;

    if (name === 'street') finalValue = formatStreetAddress(value);
    if (name === 'city') finalValue = formatCityName(value);
    if (name === 'zip') finalValue = formatZipCode(value);

    if (finalValue !== value) {
      setBilling(prev => ({ ...prev, [name]: finalValue }));
    }

    setBillingTouched(prev => ({ ...prev, [name]: true }));
    validateBillingField(name, finalValue);
  };

  const handleStateChange = (e) => {
    const selected = e.target.value;
    setBilling(prev => ({ ...prev, state: selected }));
    setBillingTouched(prev => ({ ...prev, state: true }));
    validateBillingField('state', selected);
  };

  // ========================================================================
  // Form Submission
  // ========================================================================

  const handleSubmit = (e) => {
    e.preventDefault();

    const newErrors = {};
    if (!billing.street || billing.street.trim().length < 5) newErrors.street = 'Billing address is required (minimum 5 characters)';
    if (!billing.city || billing.city.trim().length < 2) newErrors.city = 'City is required (minimum 2 characters)';
    if (!billing.state || !isValidStateCode(billing.state)) newErrors.state = 'Please select a valid state';
    if (!billing.zip || !isValidZipCode(billing.zip)) newErrors.zip = 'Please enter a valid ZIP code (5 digits or ZIP+4 format)';

    setBillingErrors(newErrors);
    
    const billingFields = ['street', 'city', 'state', 'zip'];
    const allTouched = billingFields.reduce((acc, f) => ({ ...acc, [f]: true }), {});
    setBillingTouched(allTouched);

    if (Object.keys(newErrors).length > 0) return;

    // Submit HPF fields + billing address
    hpfSubmit({
      fullName: '', // HPF has the cardholder name in the iFrame
      street1: formatStreetAddress(billing.street),
      city: formatCityName(billing.city),
      region: billing.state.toUpperCase(),
      postalCode: formatZipCode(billing.zip),
      countryCode: 'USA'
    });
  };

  // ========================================================================
  // Styling (matches PaymentCardForm exactly)
  // ========================================================================

  const getInputClasses = (hasError) => {
    const base = 'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 transition-colors';
    const err = hasError ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-blue-200 focus:border-blue-500';
    const bg = theme === 'portal' ? 'bg-gray-50' : 'bg-white';
    return `${base} ${err} ${bg}`;
  };

  const getHpfWrapperClasses = (fieldName) => {
    const base = 'w-full border rounded-md transition-colors overflow-hidden';
    const bg = theme === 'portal' ? 'bg-gray-50' : 'bg-white';

    if (fieldValidity[fieldName] === false) {
      return `${base} border-red-500 ${bg}`;
    }
    // null (untouched) or true (valid)
    return `${base} border-gray-300 ${bg}`;
  };

  // All fields loaded — show skeletons until every iFrame is ready so they appear together
  const allFieldsLoaded = Object.values(fieldsLoaded).every(Boolean);

  // Skeleton loader — visible until ALL iFrame fields have loaded
  const renderSkeleton = (width = 'w-3/4') => {
    if (allFieldsLoaded) return null;
    return (
      <span className="absolute inset-0 flex items-center px-3 pointer-events-none">
        <span className={`${width} h-4 bg-gray-200 rounded animate-pulse`} />
      </span>
    );
  };

  const getButtonClasses = () => {
    const base = 'w-full py-3 px-4 rounded-md font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2';
    const colors = theme === 'portal'
      ? 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500 text-white'
      : 'bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500 text-white';
    const isDisabled = processing || !isReady || isRecovering || isSubmitting;
    const disabled = isDisabled ? 'opacity-50 cursor-not-allowed' : '';
    return `${base} ${colors} ${disabled}`;
  };

  /**
   * Contextual button text based on current state.
   */
  const getButtonContent = () => {
    if (isRecovering) {
      return (
        <span className="flex items-center justify-center">
          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Reloading Payment Fields...
        </span>
      );
    }
    if (processing || isSubmitting) {
      return (
        <span className="flex items-center justify-center">
          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Processing...
        </span>
      );
    }
    if (!isReady) {
      return (
        <span className="flex items-center justify-center">
          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Loading Payment Fields...
        </span>
      );
    }
    return submitButtonText;
  };

  // ========================================================================
  // Render
  // ========================================================================

  // Initialization error — show fallback instead of broken iFrames
  if (hpfError?.type === 'initError') {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
        <p className="font-medium">Unable to load secure payment fields</p>
        <p className="text-sm mt-1">{hpfError.message}</p>
        <p className="text-sm mt-2">Please refresh the page or contact support.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Global error display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error}
        </div>
      )}

      {/* HPF SDK error display */}
      {hpfError && hpfError.type !== 'initError' && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {hpfError.userMessage || hpfError.message || 'A payment error occurred'}
        </div>
      )}

      {/* Card Information Section — HPF iFrame wrappers */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">Card Information</h3>

        {/* Card Number — HPF iFrame */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Card Number *
          </label>
          <div
            id="hpf-card-number"
            className={`relative ${getHpfWrapperClasses('cardNumber')}`}
            style={{ height: '38px' }}
          >
            {renderSkeleton('w-3/4')}
          </div>
          {fieldValidity.cardNumber === false && (
            <p className="mt-1 text-sm text-red-600">Please enter a valid card number</p>
          )}
        </div>

        {/* Expiry and CVV Row — HPF iFrames */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Expiration Date *
            </label>
            <div
              id="hpf-expiry"
              className={`relative ${getHpfWrapperClasses('cardExpirationDate')}`}
              style={{ height: '38px' }}
            >
              {renderSkeleton('w-1/2')}
            </div>
            {fieldValidity.cardExpirationDate === false && (
              <p className="mt-1 text-sm text-red-600">Please enter a valid expiration date</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              CVV *
            </label>
            <div
              id="hpf-cvv"
              className={`relative ${getHpfWrapperClasses('cardCvv')}`}
              style={{ height: '38px' }}
            >
              {renderSkeleton('w-1/3')}
            </div>
            {fieldValidity.cardCvv === false && (
              <p className="mt-1 text-sm text-red-600">Please enter a valid CVV</p>
            )}
          </div>
        </div>

        {/* Cardholder Name — HPF iFrame */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Cardholder Name *
          </label>
          <div
            id="hpf-cardholder-name"
            className={`relative ${getHpfWrapperClasses('cardholderName')}`}
            style={{ height: '38px' }}
          >
            {renderSkeleton('w-1/2')}
          </div>
          {fieldValidity.cardholderName === false && (
            <p className="mt-1 text-sm text-red-600">Please enter the cardholder name</p>
          )}
        </div>
      </div>

      {/* Billing Address Section — Local HTML inputs (same as PaymentCardForm) */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">Billing Address</h3>

        {/* Street Address */}
        <div>
          <label htmlFor="hpf-billing-street" className="block text-sm font-medium text-gray-700 mb-1">
            Street Address *
          </label>
          <input
            type="text"
            id="hpf-billing-street"
            name="street"
            value={billing.street}
            onChange={handleBillingChange}
            onBlur={handleBillingBlur}
            placeholder="123 Main St"
            className={getInputClasses(billingErrors.street && billingTouched.street)}
            disabled={processing}
          />
          {billingErrors.street && billingTouched.street && (
            <p className="mt-1 text-sm text-red-600">{billingErrors.street}</p>
          )}
        </div>

        {/* City and State Row */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="hpf-billing-city" className="block text-sm font-medium text-gray-700 mb-1">
              City *
            </label>
            <input
              type="text"
              id="hpf-billing-city"
              name="city"
              value={billing.city}
              onChange={handleBillingChange}
              onBlur={handleBillingBlur}
              placeholder="Kalispell"
              className={getInputClasses(billingErrors.city && billingTouched.city)}
              disabled={processing}
            />
            {billingErrors.city && billingTouched.city && (
              <p className="mt-1 text-sm text-red-600">{billingErrors.city}</p>
            )}
          </div>

          <div>
            <label htmlFor="hpf-billing-state" className="block text-sm font-medium text-gray-700 mb-1">
              State *
            </label>
            <select
              id="hpf-billing-state"
              name="state"
              value={billing.state}
              onChange={handleStateChange}
              onBlur={handleBillingBlur}
              className={getInputClasses(billingErrors.state && billingTouched.state)}
              disabled={processing}
            >
              <option value="">Select State</option>
              {US_STATES.map(state => (
                <option key={state.code} value={state.code}>
                  {state.code} - {state.name}
                </option>
              ))}
            </select>
            {billingErrors.state && billingTouched.state && (
              <p className="mt-1 text-sm text-red-600">{billingErrors.state}</p>
            )}
          </div>
        </div>

        {/* ZIP Code */}
        <div className="w-1/2">
          <label htmlFor="hpf-billing-zip" className="block text-sm font-medium text-gray-700 mb-1">
            ZIP Code *
          </label>
          <input
            type="text"
            id="hpf-billing-zip"
            name="zip"
            value={billing.zip}
            onChange={handleBillingChange}
            onBlur={handleBillingBlur}
            placeholder="59901"
            className={getInputClasses(billingErrors.zip && billingTouched.zip)}
            disabled={processing}
          />
          {billingErrors.zip && billingTouched.zip && (
            <p className="mt-1 text-sm text-red-600">{billingErrors.zip}</p>
          )}
        </div>
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={processing || !isReady || isRecovering || isSubmitting}
        className={getButtonClasses()}
      >
        {getButtonContent()}
      </button>

      {/* Security Notice */}
      <div className="text-xs text-gray-500 text-center">
        <p>Your payment information is encrypted and secure. Card data is handled by our PCI-certified payment processor and never touches our servers.</p>
      </div>

      {/* Surcharge consent modal removed — surcharge is now handled server-side.
          The surcharge consent UI is rendered by the parent component (PortalPayment)
          after receiving surcharge info from the vault-and-check-surcharge endpoint. */}
    </form>
  );
}

export default HostedPaymentFields;
